import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import AppShell from '../shell/AppShell';
import { UserContext } from '../../UserContext';
import dataService from '../../services/dataService';
import { useI18n } from '../../features/i18n';
import '../../features/brand/cwm-tokens.css';

/**
 * /unified/onboarding — 3-step first-run wizard that seeds the user's profile
 * so chat + discovery work meaningfully from day one. Routes here automatically
 * after signup; users can also reach it from profile to re-take.
 *
 * Steps:
 *   1. Allergies — 12 quick-add chips + severity per entry
 *   2. Preferences — diet style + cuisines + like/love
 *   3. Dining context — who you dine with, eating-out frequency, ambition
 *
 * On finish: saves all three to the existing dataService endpoints
 * (saveUserPreferences with restriction_type 'allergy' | 'preference' |
 * 'dining_context') and drops a flag in localStorage so the wizard doesn't
 * re-fire next login.
 */

const EASE = [0.22, 1, 0.36, 1];
const ONBOARDING_DONE_KEY = 'cwm_onboarding_done';

const COMMON_ALLERGENS = [
  { label: 'Peanut',     emoji: 'Pe' },
  { label: 'Tree nuts',  emoji: 'TN' },
  { label: 'Milk',       emoji: 'Mi' },
  { label: 'Egg',        emoji: 'Eg' },
  { label: 'Wheat',      emoji: 'Wh' },
  { label: 'Gluten',     emoji: 'Gl' },
  { label: 'Soy',        emoji: 'So' },
  { label: 'Fish',       emoji: 'Fi' },
  { label: 'Shellfish',  emoji: 'Sh' },
  { label: 'Sesame',     emoji: 'Se' },
  { label: 'Mustard',    emoji: 'Mu' },
  { label: 'Sulfites',   emoji: 'Su' },
];

const SEVERITY_LEVELS = [
  { id: 'mild',             label: 'Mild',             level: 0.1, tone: 'amber'    },
  { id: 'moderate',         label: 'Moderate',         level: 0.4, tone: 'orange'   },
  { id: 'severe',           label: 'Severe',           level: 0.7, tone: 'red'      },
  { id: 'life_threatening', label: 'Life-threatening', level: 1.0, tone: 'critical' },
];

const SEVERITY_TONES = {
  amber:    { bg: 'var(--cwm-amber-light)', fg: 'var(--cwm-amber-text)', solid: 'var(--cwm-amber)' },
  orange:   { bg: '#FFE8D6',                fg: '#8A4A00',               solid: '#EA580C' },
  red:      { bg: 'var(--cwm-red-light)',   fg: 'var(--cwm-red-text)',   solid: '#B91C1C' },
  critical: { bg: '#1F1314',                fg: '#FFE8E8',               solid: '#7F1D1D' },
};

const DIETARY_STYLES = ['Vegetarian', 'Vegan', 'Pescatarian', 'Keto', 'Paleo', 'Mediterranean', 'Gluten-Free', 'No restrictions'];
const CUISINES = ['Italian', 'Japanese', 'Mexican', 'Thai', 'Indian', 'French', 'Chinese', 'Mediterranean', 'American', 'Middle Eastern', 'Korean', 'Vietnamese'];

const DINING_CONTEXTS = [
  { id: 'solo',     label: 'Mostly solo',        emoji: '👤' },
  { id: 'partner',  label: 'With a partner',     emoji: '💑' },
  { id: 'kids',     label: 'With kids',          emoji: '👨\u200d👩\u200d👧' },
  { id: 'friends',  label: 'With friends',       emoji: '👥' },
  { id: 'business', label: 'Business dinners',   emoji: '💼' },
];

const DINING_FREQUENCY = [
  { id: 'occasional', label: 'Once a month',        sub: 'Special occasions' },
  { id: 'regular',    label: '2-3× a month',        sub: 'Fairly regular' },
  { id: 'frequent',   label: 'Weekly',              sub: 'I eat out a lot' },
  { id: 'daily',      label: 'Multiple times/week', sub: 'Basically every day' },
];

const DINING_AMBITION = [
  { id: 'safe',       label: 'Safe & familiar',   sub: "Stick to what I know works" },
  { id: 'balanced',   label: 'Balanced',          sub: "Safe basics, occasional new dishes" },
  { id: 'adventurous',label: 'Adventurous',       sub: 'Try anything new that fits my needs' },
];

export default function UnifiedOnboarding() {
  const navigate = useNavigate();
  const { userId, isInitialized } = useContext(UserContext);
  const { t } = useI18n();

  const [step, setStep] = useState(1);
  const [allergies, setAllergies] = useState([]); // [{ingredient, level, severity}]
  const [customAllergen, setCustomAllergen] = useState('');
  const [dietStyle, setDietStyle] = useState([]);
  const [cuisines, setCuisines] = useState([]); // [{ingredient, level}]
  const [context, setContext] = useState(null);
  const [frequency, setFrequency] = useState(null);
  const [ambition, setAmbition] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isInitialized) return;
    if (!userId) navigate('/unified');
  }, [userId, isInitialized, navigate]);

  const addAllergen = (name, severityId = 'moderate') => {
    if (!name.trim()) return;
    if (allergies.some((a) => a.ingredient.toLowerCase() === name.toLowerCase())) return;
    const sev = SEVERITY_LEVELS.find((s) => s.id === severityId) || SEVERITY_LEVELS[1];
    setAllergies((prev) => [...prev, { ingredient: name.trim(), level: sev.level, severity: severityId }]);
  };
  const removeAllergen = (name) => setAllergies((prev) => prev.filter((a) => a.ingredient !== name));
  const updateSeverity = (name, severityId) => {
    const sev = SEVERITY_LEVELS.find((s) => s.id === severityId) || SEVERITY_LEVELS[1];
    setAllergies((prev) => prev.map((a) => a.ingredient === name ? { ...a, level: sev.level, severity: severityId } : a));
  };

  const toggleDietStyle = (s) => setDietStyle((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);
  const toggleCuisine = (c) => {
    setCuisines((prev) => {
      const i = prev.findIndex((x) => x.ingredient === c);
      if (i >= 0) return prev.filter((x) => x.ingredient !== c);
      return [...prev, { ingredient: c, level: 0.7 }];
    });
  };

  const finish = async () => {
    setSaving(true);
    setError(null);
    try {
      const prefPayload = [
        ...dietStyle.map((s) => ({ ingredient: s, level: 0.8 })),
        ...cuisines,
      ];
      const contextPayload = [
        context    && { ingredient: `context:${context}`,     level: 1 },
        frequency  && { ingredient: `frequency:${frequency}`, level: 1 },
        ambition   && { ingredient: `ambition:${ambition}`,   level: 1 },
      ].filter(Boolean);

      await Promise.all([
        dataService.saveUserPreferences(userId, allergies.map(({ ingredient, level }) => ({ ingredient, level })), 'allergy').catch(() => {}),
        dataService.saveUserPreferences(userId, prefPayload, 'preference').catch(() => {}),
        dataService.saveUserPreferences(userId, contextPayload, 'dining_context').catch(() => {}),
      ]);
      localStorage.setItem(ONBOARDING_DONE_KEY, '1');
      navigate('/unified/dashboard');
    } catch (e) {
      setError(t('onboarding.error_save'));
    } finally {
      setSaving(false);
    }
  };

  const skip = () => {
    localStorage.setItem(ONBOARDING_DONE_KEY, '1');
    navigate('/unified/dashboard');
  };

  return (
    <AppShell backdrop footer={false}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '28px 20px 80px', position: 'relative', zIndex: 1 }}>

        {/* Progress + skip */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div className="cwm-eyebrow">{t('onboarding.step_of', { step })}</div>
          <button type="button" onClick={skip}
                  style={{
                    padding: '6px 14px', borderRadius: 999,
                    background: 'transparent', color: 'var(--cwm-gray-500)',
                    border: 'none',
                    fontFamily: 'var(--cwm-font)', fontSize: 12, fontWeight: 800,
                    cursor: 'pointer', textDecoration: 'underline',
                  }}>
            {t('onboarding.skip')}
          </button>
        </div>
        <div style={{ height: 6, borderRadius: 999, background: 'var(--cwm-gray-100)', overflow: 'hidden', marginBottom: 24 }}>
          <motion.div
            initial={false}
            animate={{ width: `${(step / 3) * 100}%` }}
            transition={{ duration: 0.46, ease: EASE }}
            style={{ height: '100%', background: 'linear-gradient(90deg, #1E9BFF, #0F7FE8)' }}
          />
        </div>

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div
              key="s1"
              initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.34, ease: EASE }}
            >
              <Step1Allergies
                t={t}
                allergies={allergies}
                customAllergen={customAllergen}
                setCustomAllergen={setCustomAllergen}
                addAllergen={addAllergen}
                removeAllergen={removeAllergen}
                updateSeverity={updateSeverity}
              />
            </motion.div>
          )}
          {step === 2 && (
            <motion.div
              key="s2"
              initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.34, ease: EASE }}
            >
              <Step2Preferences
                t={t}
                dietStyle={dietStyle}
                toggleDietStyle={toggleDietStyle}
                cuisines={cuisines}
                toggleCuisine={toggleCuisine}
              />
            </motion.div>
          )}
          {step === 3 && (
            <motion.div
              key="s3"
              initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.34, ease: EASE }}
            >
              <Step3Context
                t={t}
                context={context} setContext={setContext}
                frequency={frequency} setFrequency={setFrequency}
                ambition={ambition} setAmbition={setAmbition}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {error && <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 14, background: 'var(--cwm-red-light)', color: 'var(--cwm-red-text)', fontSize: 13, fontWeight: 800 }}>{error}</div>}

        {/* Nav footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 28, paddingTop: 18, borderTop: '1px solid var(--cwm-gray-100)' }}>
          {step > 1 ? (
            <button className="cwm-btn-secondary" onClick={() => setStep(step - 1)}>
              <ArrowLeftIcon size={12} /> {t('onboarding.back')}
            </button>
          ) : (
            <div/>
          )}
          {step < 3 ? (
            <button className="cwm-btn" onClick={() => setStep(step + 1)}>
              {t('onboarding.continue')} <ArrowRightIcon size={12} />
            </button>
          ) : (
            <button className="cwm-btn" onClick={finish} disabled={saving}
                    style={{ opacity: saving ? 0.6 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? <Dots /> : <><CheckIcon size={12} /> {t('onboarding.finish')}</>}
            </button>
          )}
        </div>
      </div>
    </AppShell>
  );
}

// ============ STEP 1: ALLERGIES ============

function Step1Allergies({ t, allergies, customAllergen, setCustomAllergen, addAllergen, removeAllergen, updateSeverity }) {
  const has = (name) => allergies.some((a) => a.ingredient.toLowerCase() === name.toLowerCase());

  return (
    <div>
      <StepHeader
        tone="red"
        icon="shield"
        eyebrow={t('onboarding.s1_eyebrow')}
        title={t('onboarding.s1_title')}
        body={t('onboarding.s1_body')}
      />

      <div className="cwm-sheet" style={{ padding: 20, marginBottom: 14 }}>
        <div className="cwm-eyebrow" style={{ color: 'var(--cwm-red-text)', marginBottom: 10 }}>
          {t('onboarding.s1_common_eyebrow')}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
          {COMMON_ALLERGENS.map((a) => {
            const on = has(a.label);
            return (
              <motion.button
                key={a.label} type="button"
                onClick={() => on ? removeAllergen(a.label) : addAllergen(a.label, 'moderate')}
                whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '7px 14px 7px 7px', borderRadius: 999,
                  background: on ? '#B91C1C' : '#fff',
                  color: on ? '#fff' : 'var(--cwm-red-text)',
                  border: `1.5px solid ${on ? '#B91C1C' : '#F4B6B6'}`,
                  fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 800,
                  cursor: 'pointer',
                  boxShadow: on ? '0 4px 10px rgba(185,28,28,0.32)' : 'none',
                  transition: 'all 140ms ease',
                }}>
                <span style={{
                  width: 22, height: 22, borderRadius: '50%',
                  background: on ? 'rgba(255,255,255,0.22)' : 'var(--cwm-red-light)',
                  color: on ? '#fff' : 'var(--cwm-red-text)',
                  display: 'grid', placeItems: 'center',
                  fontSize: 9.5, fontWeight: 900,
                }}>{on ? <CheckIcon size={10} /> : a.emoji}</span>
                {a.label}
              </motion.button>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={customAllergen}
            onChange={(e) => setCustomAllergen(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAllergen(customAllergen); setCustomAllergen(''); } }}
            placeholder={t('onboarding.s1_custom_placeholder')}
            style={{
              flex: 1, minWidth: 0,
              padding: '10px 14px', borderRadius: 12,
              border: '1.5px solid #F4B6B6', background: 'var(--cwm-red-light)',
              color: 'var(--cwm-ink)',
              fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 600,
              outline: 'none',
            }}/>
          <button
            type="button" onClick={() => { addAllergen(customAllergen); setCustomAllergen(''); }}
            disabled={!customAllergen.trim()}
            style={{
              padding: '10px 14px', borderRadius: 12,
              background: customAllergen.trim() ? '#B91C1C' : 'var(--cwm-gray-100)',
              color: customAllergen.trim() ? '#fff' : 'var(--cwm-gray-500)',
              border: 'none', cursor: customAllergen.trim() ? 'pointer' : 'not-allowed',
              fontFamily: 'var(--cwm-font)', fontSize: 12.5, fontWeight: 900,
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}>
            <PlusIcon size={11} /> {t('onboarding.s1_add')}
          </button>
        </div>
      </div>

      {allergies.length > 0 && (
        <div>
          <div className="cwm-eyebrow" style={{ marginBottom: 10 }}>{t('onboarding.s1_your_list', { count: allergies.length })}</div>
          <div style={{ display: 'grid', gap: 8 }}>
            <AnimatePresence initial={false}>
              {allergies.map((a) => (
                <motion.div
                  key={a.ingredient}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: 30 }}
                  transition={{ duration: 0.24 }}
                  className="cwm-sheet" style={{ padding: 12 }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--cwm-ink)' }}>
                      ⚠ {a.ingredient}
                    </div>
                    <button type="button" onClick={() => removeAllergen(a.ingredient)}
                            style={{ background: 'transparent', border: 'none', color: 'var(--cwm-gray-500)', cursor: 'pointer', padding: 2 }}>
                      <TrashIcon size={12} />
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {SEVERITY_LEVELS.map((s) => {
                      const on = s.id === a.severity;
                      const st = SEVERITY_TONES[s.tone];
                      return (
                        <button key={s.id} type="button" onClick={() => updateSeverity(a.ingredient, s.id)}
                                style={{
                                  padding: '5px 11px', borderRadius: 999,
                                  background: on ? st.solid : st.bg, color: on ? '#fff' : st.fg,
                                  border: `1.5px solid ${on ? st.solid : 'transparent'}`,
                                  fontFamily: 'var(--cwm-font)', fontSize: 11, fontWeight: 900,
                                  cursor: 'pointer', transition: 'all 140ms ease',
                                }}>
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ STEP 2: PREFERENCES ============

function Step2Preferences({ t, dietStyle, toggleDietStyle, cuisines, toggleCuisine }) {
  return (
    <div>
      <StepHeader
        tone="green"
        icon="heart"
        eyebrow={t('onboarding.s2_eyebrow')}
        title={t('onboarding.s2_title')}
        body={t('onboarding.s2_body')}
      />

      <div className="cwm-sheet" style={{ padding: 20, marginBottom: 12 }}>
        <div className="cwm-eyebrow" style={{ color: 'var(--cwm-green-text)', marginBottom: 8 }}>{t('onboarding.s2_diet_eyebrow')}</div>
        <div style={{ fontSize: 12.5, color: 'var(--cwm-gray-500)', fontWeight: 600, marginBottom: 12 }}>
          Pick any that describe how you eat, or none.
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {DIETARY_STYLES.map((s) => {
            const on = dietStyle.includes(s);
            return (
              <motion.button
                key={s} type="button" onClick={() => toggleDietStyle(s)}
                whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
                style={{
                  padding: '7px 14px', borderRadius: 999,
                  background: on ? 'var(--cwm-green)' : '#fff',
                  color: on ? '#fff' : 'var(--cwm-green-text)',
                  border: `1.5px solid ${on ? 'var(--cwm-green)' : '#A6E8C2'}`,
                  fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 800,
                  cursor: 'pointer', transition: 'all 140ms ease',
                  boxShadow: on ? '0 4px 10px rgba(22,128,60,0.28)' : 'none',
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                }}>
                {on && <CheckIcon size={10} />} {s}
              </motion.button>
            );
          })}
        </div>
      </div>

      <div className="cwm-sheet" style={{ padding: 20 }}>
        <div className="cwm-eyebrow" style={{ color: 'var(--cwm-blue-deep)', marginBottom: 8 }}>{t('onboarding.s2_cuisines_eyebrow')}</div>
        <div style={{ fontSize: 12.5, color: 'var(--cwm-gray-500)', fontWeight: 600, marginBottom: 12 }}>
          Pick all the cuisines you gravitate toward. {cuisines.length ? `${cuisines.length} selected.` : ''}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {CUISINES.map((c) => {
            const on = cuisines.some((x) => x.ingredient === c);
            return (
              <motion.button
                key={c} type="button" onClick={() => toggleCuisine(c)}
                whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
                style={{
                  padding: '7px 14px', borderRadius: 999,
                  background: on ? 'linear-gradient(90deg, #1E9BFF, #0F7FE8)' : '#fff',
                  color: on ? '#fff' : 'var(--cwm-blue-deep)',
                  border: `1.5px solid ${on ? 'var(--cwm-blue-deep)' : 'var(--cwm-blue-light)'}`,
                  fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 800,
                  cursor: 'pointer', transition: 'all 140ms ease',
                  boxShadow: on ? '0 4px 10px rgba(15,127,232,0.28)' : 'none',
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                }}>
                {on && <CheckIcon size={10} />} {c}
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============ STEP 3: DINING CONTEXT ============

function Step3Context({ t, context, setContext, frequency, setFrequency, ambition, setAmbition }) {
  return (
    <div>
      <StepHeader
        tone="purple"
        icon="people"
        eyebrow={t('onboarding.s3_eyebrow')}
        title={t('onboarding.s3_title')}
        body={t('onboarding.s3_body')}
      />

      <div className="cwm-sheet" style={{ padding: 20, marginBottom: 12 }}>
        <div className="cwm-eyebrow" style={{ color: '#5B21B6', marginBottom: 10 }}>{t('onboarding.s3_who_eyebrow')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 8 }}>
          {DINING_CONTEXTS.map((c) => {
            const on = context === c.id;
            return (
              <motion.button key={c.id} type="button" onClick={() => setContext(c.id)}
                whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
                style={{
                  padding: '14px 12px', borderRadius: 16,
                  background: on ? 'linear-gradient(160deg, #F1E8FE, #CFBAF0)' : '#fff',
                  color: on ? '#5B21B6' : 'var(--cwm-ink)',
                  border: `1.5px solid ${on ? '#7C3AED' : '#E5E7EB'}`,
                  fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 800,
                  cursor: 'pointer', transition: 'all 140ms ease',
                  textAlign: 'center',
                  boxShadow: on ? '0 6px 14px rgba(124,58,237,0.22)' : 'none',
                }}>
                <div style={{ fontSize: 22, marginBottom: 4 }}>{c.emoji}</div>
                {c.label}
              </motion.button>
            );
          })}
        </div>
      </div>

      <div className="cwm-sheet" style={{ padding: 20, marginBottom: 12 }}>
        <div className="cwm-eyebrow" style={{ color: 'var(--cwm-blue-deep)', marginBottom: 10 }}>{t('onboarding.s3_how_often_eyebrow')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
          {DINING_FREQUENCY.map((f) => {
            const on = frequency === f.id;
            return (
              <motion.button key={f.id} type="button" onClick={() => setFrequency(f.id)}
                whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
                style={{
                  padding: '12px 14px', borderRadius: 14,
                  background: on ? 'var(--cwm-blue-pale)' : '#fff',
                  border: `1.5px solid ${on ? 'var(--cwm-blue-deep)' : 'var(--cwm-blue-light)'}`,
                  fontFamily: 'var(--cwm-font)', cursor: 'pointer', transition: 'all 140ms ease',
                  textAlign: 'left',
                }}>
                <div style={{ fontSize: 13.5, fontWeight: 900, color: 'var(--cwm-ink)' }}>{f.label}</div>
                <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--cwm-gray-500)', marginTop: 2 }}>{f.sub}</div>
              </motion.button>
            );
          })}
        </div>
      </div>

      <div className="cwm-sheet" style={{ padding: 20 }}>
        <div className="cwm-eyebrow" style={{ color: 'var(--cwm-amber-text)', marginBottom: 10 }}>{t('onboarding.s3_ambition_eyebrow')}</div>
        <div style={{ display: 'grid', gap: 8 }}>
          {DINING_AMBITION.map((a) => {
            const on = ambition === a.id;
            return (
              <motion.button key={a.id} type="button" onClick={() => setAmbition(a.id)}
                whileHover={{ x: 3 }} whileTap={{ scale: 0.98 }}
                style={{
                  padding: '14px 16px', borderRadius: 14,
                  background: on ? 'linear-gradient(90deg, var(--cwm-amber-light), #FFE0B8)' : '#fff',
                  border: `1.5px solid ${on ? 'var(--cwm-amber)' : 'var(--cwm-gray-200)'}`,
                  fontFamily: 'var(--cwm-font)', cursor: 'pointer', transition: 'all 140ms ease',
                  textAlign: 'left',
                  boxShadow: on ? '0 4px 12px rgba(180,83,9,0.18)' : 'none',
                }}>
                <div style={{ fontSize: 13.5, fontWeight: 900, color: 'var(--cwm-ink)' }}>{a.label}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--cwm-gray-700)', marginTop: 2 }}>{a.sub}</div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============ HEADER ============

function StepHeader({ tone, icon, eyebrow, title, body }) {
  const tones = {
    red:    { bg: 'var(--cwm-red-light)',   fg: 'var(--cwm-red-text)',   solid: '#B91C1C' },
    green:  { bg: 'var(--cwm-green-light)', fg: 'var(--cwm-green-text)', solid: 'var(--cwm-green)' },
    purple: { bg: '#F1E8FE',                fg: '#5B21B6',               solid: '#7C3AED' },
  };
  const pal = tones[tone] || tones.green;
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '6px 14px', borderRadius: 999,
        background: pal.bg, color: pal.fg,
        fontSize: 11, fontWeight: 900, letterSpacing: '0.14em', textTransform: 'uppercase',
        marginBottom: 12,
      }}>
        <HeaderIcon name={icon} size={12} /> {eyebrow}
      </div>
      <h1 style={{ fontSize: 30, fontWeight: 900, letterSpacing: '-0.025em', color: 'var(--cwm-ink)', margin: '0 0 8px', lineHeight: 1.1 }}>
        {title}
      </h1>
      <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.55, color: 'var(--cwm-gray-700)', fontWeight: 500 }}>
        {body}
      </p>
    </div>
  );
}

// ============ ICONS + UTILS ============

function Dots() {
  return (
    <span style={{ display: 'inline-flex', gap: 4 }}>
      {[0, 1, 2].map((i) => <span key={i} style={{ width: 6, height: 6, borderRadius: 999, background: '#fff', animation: 'cwmDot 900ms ease-in-out infinite', animationDelay: `${i * 150}ms` }}/>)}
    </span>
  );
}

function HeaderIcon({ name, size = 12 }) {
  const paths = {
    shield: 'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z',
    heart:  'M12 21s-7-5.5-7-11a4 4 0 017-2.6A4 4 0 0119 10c0 5.5-7 11-7 11z',
    people: 'M9 11a3 3 0 100-6 3 3 0 000 6zM17 12a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM3 19c0-3 3-5 6-5s6 2 6 5M16 19c0-2 1.5-4 4-4',
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill={name === 'heart' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d={paths[name] || paths.shield}/></svg>;
}

function CheckIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg>; }
function PlusIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>; }
function TrashIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>; }
function ArrowLeftIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M11 6l-6 6 6 6"/></svg>; }
function ArrowRightIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>; }
